/**
 * Auth Hooks
 *
 * React Query hooks for authentication.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSdkContext } from '../providers/SdkProvider';
import { useAuth as useSdkAuth } from '../providers/AuthProvider';
import { createAuthService } from '../services/auth.service';

// Query keys
export const authKeys = {
  all: ['auth'] as const,
  user: () => [...authKeys.all, 'user'] as const,
  portals: () => [...authKeys.all, 'portals'] as const,
};

/**
 * Get current authenticated user
 */
export function useCurrentUser() {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useSdkAuth();
  const authService = createAuthService(client);

  return useQuery({
    queryKey: authKeys.user(),
    queryFn: () => authService.getCurrentUser(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false, // Don't retry auth errors
    enabled: isSignedIn && !!token,
  });
}

/**
 * Portal memberships for the current user.
 *
 * Portals (`journey`, `business`) are a product concept; a single Clerk
 * identity can hold access to more than one. The frontend uses this to
 * decide whether to show the portal UI or a "no access" / "switch account"
 * screen when cross-portal sign-in is detected.
 */
export function useMyPortals() {
  const { client } = useSdkContext();
  const { isSignedIn, token } = useSdkAuth();
  const authService = createAuthService(client);

  return useQuery({
    queryKey: authKeys.portals(),
    queryFn: () => authService.getMyPortals(),
    staleTime: 1000 * 60 * 5, // Portal access changes rarely mid-session
    retry: false,
    enabled: isSignedIn && !!token,
  });
}

/**
 * Verify token mutation
 */
export function useVerifyToken() {
  const { client } = useSdkContext();
  const authService = createAuthService(client);

  return useMutation({
    mutationFn: (token: string) => authService.verifyToken(token),
  });
}

/**
 * Logout mutation
 */
export function useLogout() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const authService = createAuthService(client);

  return useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      // Clear all cached data on logout
      queryClient.clear();
      client.setAuthToken(null);
    },
  });
}

/**
 * Check if user is authenticated
 */
export function useIsAuthenticated(): boolean {
  const { data: user, isLoading } = useCurrentUser();
  return !isLoading && !!user;
}

// Password reset is handled directly by Clerk's useSignIn hook
// with the reset_password_email_code strategy. No custom backend needed.
